import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PageHero } from '../components/PageHero';
import RCPageHeader from '../components/RCPageHeader';
import { PortfolioBanner } from '../components/PageBanners';
import { portfolioCases } from '../data/mock';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp } from 'lucide-react';

const CATEGORIES = ['Alle', 'SEO & Content', 'Social Media', 'Google Ads', 'Lead Generatie'];

const PortfolioVisual = () => (
  <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
    {[
      { value: '238+', label: 'Succescases', color: 'bg-[#D4A017]' },
      { value: '€18M+', label: 'Omzet gegenereerd', color: 'bg-white/10' },
      { value: '95%', label: 'Klantretentie', color: 'bg-white/10' },
      { value: '4', label: 'Specialisaties', color: 'bg-white/10' },
    ].map(({ value, label, color }) => (
      <div key={label} className={`${color} rounded-2xl p-5 flex flex-col`}>
        <span className={`text-2xl font-bold ${color === 'bg-[#D4A017]' ? 'text-[#0f172a]' : 'text-white'}`}>{value}</span>
        <span className={`text-xs mt-1 ${color === 'bg-[#D4A017]' ? 'text-[#0f172a]/70' : 'text-[#94a3b8]'}`}>{label}</span>
      </div>
    ))}
  </div>
);

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [visible, setVisible] = useState(24);

  const filtered = activeCategory === 'Alle'
    ? portfolioCases
    : portfolioCases.filter(c => c.category === activeCategory);

  return (
    <>
      <SEOHead
        title="Portfolio — 238+ Succesvolle Marketingcampagnes"
        description="Bekijk de resultaten van IMPERIA AI: 238+ succesvolle campagnes voor bedrijven in SEO, Google Ads, social media en lead generatie. Van startups tot grote organisaties."
        keywords="marketing portfolio, seo resultaten, google ads resultaten, marketing cases, klantresultaten online marketing"
        canonical="/portfolio"
      />

            <PortfolioBanner />

      {/* Remove the old stats bar — now in visual */}

      {/* Filter + Grid */}
      <section className="bg-[#f8fafc] py-16">
        <div className="container-custom">
          {/* Filter */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setVisible(24); }}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-[#0f172a] text-white'
                    : 'bg-white text-[#0f172a] border border-gray-200 hover:border-[#D4A017] hover:text-[#D4A017]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <p className="text-center text-sm text-[#64748b] mb-8">
            {filtered.length} resultaten{activeCategory !== 'Alle' ? ` in ${activeCategory}` : ''}
          </p>

          {/* Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.slice(0, visible).map((item) => (
              <article
                key={item.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={item.image}
                    alt={`Marketing resultaten voor ${item.title}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[#0f172a]/60 flex items-end p-3">
                    <span className="text-xs font-semibold text-[#D4A017] bg-[#0f172a]/70 px-2 py-1 rounded">
                      {item.category}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-[#0f172a] text-sm mb-1 truncate">{item.title}</h3>
                  <p className="text-xs text-[#64748b] mb-2 line-clamp-2">{item.description}</p>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span className="text-xs font-semibold text-green-600">{item.result}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Load more */}
          {visible < filtered.length && (
            <div className="text-center mt-10">
              <Button
                onClick={() => setVisible(v => v + 24)}
                variant="outline"
                className="border-2 border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white px-8 py-4"
              >
                Meer laden ({filtered.length - visible} resultaten)
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f172a] py-16">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Wil jij ook zulke resultaten?
          </h2>
          <p className="text-[#94a3b8] mb-8">
            Vraag een vrijblijvende offerte aan en ontdek wat IMPERIA AI voor jouw bedrijf kan betekenen.
          </p>
          <Link to="/offerte">
            <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-10 py-6 text-lg">
              Offerte aanvragen <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
